/**
 * @fileoverview Own scoped request generations for the Documents workspace. Named channels connect async results to their requesting context; obsolete responses cannot replace a newer selection. The caller owns fetching and transport cancellation. This hook supplies a predicate for applying results rather than cancelling backend work.
 */

import { useCallback, useEffect, useRef } from 'react';

// A response belongs to one document/renderer and one request channel. Starting
// another request, changing scope, or unmounting makes older responses inert.
/**
 * Call the returned channel function when beginning a request, then invoke its returned predicate before applying the result. New requests, scope changes, and teardown invalidate earlier predicates. Invalidation does not cancel server work.
 */
export default function useDocumentRequests(scope) {
  const current = useRef(null);
  if (!current.current || current.current.scope !== scope) {
    current.current = { scope, channels: new Map() };
  }
  useEffect(() => {
    // Effect setup can run again without a render during Fast Refresh.
    const context = current.current || { scope, channels: new Map() };
    current.current = context;
    return () => {
      if (current.current === context) current.current = null;
    };
  }, [scope]);
  return useCallback(

  (channel) => {
    const context = current.current;
    if (!context) return () => false;
    const request = {};
    context.channels.set(channel, request);
    return () => current.current === context && context.channels.get(channel) === request;
  }, []);
}
